"use client";

/**
 * `SelectField` — single-choice dropdown rendered as a native
 * `<select>` element styled with platform tokens. Native is the right
 * choice here for several reasons:
 *
 * - Mobile UX: native pickers are full-screen wheel pickers on iOS /
 *   Android, which beats any HTML/JS combobox we could build.
 * - Accessibility: screen-reader and keyboard support is exhaustive and
 *   uniform across platforms, with no custom ARIA wiring needed.
 * - Bundle cost: zero JS for the open/close interaction.
 *
 * The field uses rhf's `Controller` rather than `register` because the
 * native `<select>` exposes its current value via the `value` prop on
 * the DOM element. Driving it with `register({ valueAsNumber: false })`
 * works for casual cases but loses determinism around defaultValues,
 * conditional remounting, and resetForm. Controller binds value +
 * onChange + onBlur + ref directly to rhf, which is the contract every
 * other "controlled-component" field in this engine already uses
 * (Checkbox, Switch, RadioGroup, Money, Percent, etc.). Consistency
 * wins over the very small extra prop wiring.
 *
 * Placeholder behavior: when `field.placeholder` is set, the renderer
 * prepends a disabled-looking option with `value=""`. The form-state
 * value stays the empty string until the user picks something concrete.
 * Validation lives in zod — set the schema to `z.enum([...])` (no
 * empty string) or `.refine()` to require a selection.
 *
 * Styling: native selects on most desktop browsers ignore most CSS
 * properties for the dropdown panel itself; the styles below target the
 * trigger-button surface to match `<Input>`. The chevron is a CSS
 * background image rendered in the platform tokens so it picks up
 * theme colour automatically. (We deliberately don't reach for a
 * `<ChevronDown>` lucide icon overlaid via absolute positioning here —
 * the native control already paints its own chevron on every platform;
 * doubling it looked odd in our showcase prototype.)
 */

import * as React from "react";
import { Controller } from "react-hook-form";
import { cn } from "@aio/platform-ui";
import { useFormContext } from "../form-context";
import type { SelectFieldDef } from "../types";
import { FieldShell } from "./field-shell";
import { readErrorMessage } from "./read-error";
import { resolveFieldPath } from "./path";

export interface SelectFieldProps {
  field: SelectFieldDef;
  /** Path prefix from a parent {@link RepeaterField}. See `text-field.tsx`. */
  pathPrefix?: string;
}

export function SelectField({
  field,
  pathPrefix,
}: SelectFieldProps): React.ReactElement | null {
  const { form } = useFormContext();

  if (field.condition && !field.condition(form.getValues())) {
    return null;
  }

  const path = resolveFieldPath(field.name, pathPrefix);
  const errorMessage = readErrorMessage(form.formState.errors, path);

  return (
    <FieldShell
      htmlFor={path}
      label={field.label}
      {...(field.description !== undefined ? { description: field.description } : {})}
      {...(errorMessage !== undefined ? { error: errorMessage } : {})}
    >
      <Controller
        name={path}
        control={form.control}
        render={({ field: rhf }) => (
          <select
            id={path}
            ref={rhf.ref}
            value={typeof rhf.value === "string" ? rhf.value : ""}
            onChange={(event) => {
              rhf.onChange(event.target.value);
            }}
            onBlur={rhf.onBlur}
            aria-invalid={errorMessage !== undefined ? true : undefined}
            {...(field.disabled ? { disabled: true } : {})}
            className={cn(
              "flex h-[var(--aio-input-height,2.75rem)] w-full appearance-none rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg",
              "transition-colors duration-fast ease-standard",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-[invalid=true]:border-danger aria-[invalid=true]:focus-visible:ring-danger",
              // Inline-SVG chevron painted as a background; uses currentColor
              // so it follows the text colour through theme switches.
              "bg-[length:1em_1em] bg-[right_0.75rem_center] bg-no-repeat pr-9",
              "[background-image:url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22 fill=%22none%22 stroke=%22currentColor%22 stroke-width=%221.5%22><path d=%22m6 8 4 4 4-4%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/></svg>')]",
            )}
          >
            {field.placeholder !== undefined ? (
              <option value="" disabled>
                {field.placeholder}
              </option>
            ) : null}
            {field.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      />
    </FieldShell>
  );
}
